import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import {
  GapDraftReviewStatus,
  isDecidedGapReviewStatus,
} from '@momentum/types';
import type {
  EditGapDraftChangeApiRequest,
  GapDraftReviewConfidence,
  GapDraftView,
  ListGapDraftsApiResponse,
} from '@momentum/types';

import { gapReviewService } from '../services/gap-review.service';

const GAP_EXECUTIONS_QUERY_KEY = ['gap-review', 'executions'];

/**
 * The prefix every run's drafts cache hangs off. Patches are written through it
 * rather than through one run's key: a draft belongs to exactly one run, so a
 * prefix write reaches it wherever it is cached without the caller having to say
 * which run it came from.
 */
const GAP_DRAFTS_QUERY_KEY_ROOT = ['gap-review', 'drafts'];

const gapDraftsQueryKey = (executionId: string) => [
  ...GAP_DRAFTS_QUERY_KEY_ROOT,
  executionId,
];

/**
 * The final-review list: every kept draft, whichever run it came from.
 *
 * Deliberately under the drafts root rather than beside it, so a verdict patched
 * across the cached runs reaches this list too — a draft sent back to the queue
 * from the final page has to leave it without a refetch. No execution id can
 * collide with `kept`: they are all `exec-…`.
 */
const GAP_KEPT_DRAFTS_QUERY_KEY = [...GAP_DRAFTS_QUERY_KEY_ROOT, 'kept'];

const gapExecutionQueryKey = (executionId: string) => [
  'gap-review',
  'execution',
  executionId,
];

const gapDraftEditsQueryKey = (draftId: string) => [
  'gap-review',
  'draft-edits',
  draftId,
];

/**
 * Every run of the company, with the draft tally the picker shows.
 *
 * Small, and needed before anything else can be chosen — it is what the surface
 * has to show BEFORE a run's gaps are fetched, which is the whole reason the
 * counts are computed server-side rather than by tallying drafts here.
 */
export const useGapExecutions = () => {
  return useQuery({
    queryKey: GAP_EXECUTIONS_QUERY_KEY,
    queryFn: () => gapReviewService.listExecutions(),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
};

/**
 * One run's drafts, fetched when that run is selected.
 *
 * Per run rather than per company: a draft travels whole — both gap layers — so
 * the corpus-wide version of this query grew with every execution ever performed
 * while the reviewer only ever reads one of them. Each run keeps its own cache
 * entry, so stepping back to a run already visited is instant.
 *
 * Unpaged within the run and never auto-refetched while the reviewer works: the
 * queue is keyboard-driven, so a refetch landing between J and 1 would move the
 * row out from under the verdict. Verdicts are patched into this cache instead.
 */
export const useGapDrafts = (executionId: string | undefined) => {
  return useQuery({
    queryKey: gapDraftsQueryKey(executionId ?? ''),
    queryFn: () => gapReviewService.listDrafts(executionId ?? ''),
    enabled: executionId !== undefined,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
};

/**
 * Every kept draft of the company, for the final-review pass.
 *
 * Fetched by that page alone rather than by the layout: it is the one read that
 * crosses runs, and the queue has no use for it. The list is the reviewers' own
 * doing — one draft per keep — so it stays small in a way a cross-run queue
 * could not.
 *
 * Not `staleTime: Infinity` like the per-run drafts: a keep made in the queue
 * adds to this list, so it is invalidated on every verdict and refetched when
 * the page is next looked at.
 */
export const useKeptGapDrafts = () => {
  return useQuery({
    queryKey: GAP_KEPT_DRAFTS_QUERY_KEY,
    queryFn: () => gapReviewService.listKeptDrafts(),
    refetchOnWindowFocus: false,
  });
};

/** One execution's audit. Append-only once written, so it never goes stale. */
export const useGapExecution = (executionId: string) => {
  return useQuery({
    queryKey: gapExecutionQueryKey(executionId),
    queryFn: () => gapReviewService.getExecution(executionId),
    enabled: executionId.length > 0,
    staleTime: Infinity,
  });
};

/**
 * Applies `updateDraft` to the one draft matching `draftId`, in whichever run's
 * cache holds it.
 *
 * Written across every cached run rather than into one: a draft belongs to
 * exactly one execution, so this reaches it without the caller having to say
 * which — and a reviewer who has stepped between runs has more than one cached.
 */
function patchDraftInCachedRuns(
  queryClient: QueryClient,
  draftId: string,
  updateDraft: (draft: GapDraftView) => GapDraftView
): void {
  queryClient.setQueriesData<ListGapDraftsApiResponse>(
    { queryKey: GAP_DRAFTS_QUERY_KEY_ROOT },
    (cachedResponse) => {
      if (!cachedResponse) return cachedResponse;
      return {
        ...cachedResponse,
        drafts: cachedResponse.drafts.map((draft) =>
          draft._id === draftId ? updateDraft(draft) : draft
        ),
      };
    }
  );
}

/**
 * Set or clear a verdict.
 *
 * Optimistic, because the whole point of the keyboard flow is that J/1/J/1 does
 * not stutter — the row has to move to "kept" on the keypress, not on the round
 * trip. `onMutate` sets only `status`: the reviewer and their display name come
 * back from the server, and guessing them here would put a name on screen that
 * a failed request would then have to take away.
 *
 * On failure the whole previous response is restored; the caller surfaces the
 * error as a toast.
 *
 * `reviewConfidence` rides along on a verdict and on nothing else — the API
 * refuses it on a clear — so the optimistic patch drops it whenever the call
 * files no verdict, exactly as the server does.
 */
export const useSetGapDraftReview = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      draftId,
      status,
      reviewConfidence,
    }: {
      draftId: string;
      status: GapDraftReviewStatus | null;
      reviewConfidence?: GapDraftReviewConfidence;
    }) =>
      gapReviewService.setDraftReview(draftId, { status, reviewConfidence }),
    onMutate: async ({ draftId, status, reviewConfidence }) => {
      await queryClient.cancelQueries({ queryKey: GAP_DRAFTS_QUERY_KEY_ROOT });
      const previousRuns = queryClient.getQueriesData<ListGapDraftsApiResponse>(
        {
          queryKey: GAP_DRAFTS_QUERY_KEY_ROOT,
        }
      );
      patchDraftInCachedRuns(queryClient, draftId, (draft) => ({
        ...draft,
        review: {
          ...draft.review,
          status: status ?? GapDraftReviewStatus.PENDING,
          reviewConfidence: isDecidedGapReviewStatus(status)
            ? reviewConfidence
            : undefined,
        },
      }));
      return { previousRuns };
    },
    onError: (_error, _variables, mutationContext) => {
      for (const [
        queryKey,
        previousResponse,
      ] of mutationContext?.previousRuns ?? []) {
        queryClient.setQueryData(queryKey, previousResponse);
      }
    },
    onSuccess: (reviewResult, { draftId }) => {
      patchDraftInCachedRuns(queryClient, draftId, (draft) => ({
        ...draft,
        review: reviewResult.review,
      }));
      // A verdict is exactly what these two count, and neither can be patched
      // from here: keeping a draft ADDS it to the cross-run kept list, which
      // does not hold it yet, and the run tallies are counted in the database
      // for runs whose drafts were never fetched. Both are small reads, and the
      // page a verdict is typed on derives its own numbers from the drafts, so
      // nothing on screen waits for them.
      void queryClient.invalidateQueries({
        queryKey: GAP_KEPT_DRAFTS_QUERY_KEY,
      });
      void queryClient.invalidateQueries({
        queryKey: GAP_EXECUTIONS_QUERY_KEY,
      });
    },
  });
};

/**
 * One draft's edit history, fetched when the Edits tab is opened rather than
 * with the queue.
 *
 * `staleTime: Infinity` is wrong here in a way it is not for the drafts: the
 * history is appended to from this same page, so `useEditGapDraft` invalidates
 * this key on every save.
 */
export const useGapDraftEdits = (draftId: string, isEnabled: boolean) => {
  return useQuery({
    queryKey: gapDraftEditsQueryKey(draftId),
    queryFn: () => gapReviewService.listDraftEdits(draftId),
    enabled: isEnabled && draftId.length > 0,
  });
};

/**
 * Save one pass of edits over a draft's product layer.
 *
 * Not optimistic, and deliberately so. A verdict is a keystroke that has to land
 * instantly; a save is a button pressed after typing, where the round trip is
 * invisible. More to the point the server is the one that decides — it rejects
 * a path off the allow-list, an index past the end of a list, and a draft whose
 * agents have not finished — so showing the edit as applied before it answers
 * would show a change that may not exist.
 *
 * The response carries the whole `gap` layer and the whole `editedPaths` list,
 * so the cache takes both wholesale instead of merging the request back in.
 */
export const useEditGapDraft = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      draftId,
      changes,
    }: {
      draftId: string;
      changes: EditGapDraftChangeApiRequest[];
    }) => gapReviewService.editDraftGap(draftId, { changes }),
    onSuccess: (editResult, { draftId }) => {
      patchDraftInCachedRuns(queryClient, draftId, (draft) => ({
        ...draft,
        gap: editResult.gap,
        editedPaths: editResult.editedPaths,
      }));
      void queryClient.invalidateQueries({
        queryKey: gapDraftEditsQueryKey(draftId),
      });
    },
  });
};

/**
 * Append a reviewer comment.
 *
 * Not optimistic: a comment is typed rather than chorded, so the round trip
 * costs nothing perceptible, and the server owns the comment's `_id` and
 * `createdAt`. The response carries the whole comment list, so nothing here has
 * to merge an append.
 */
export const useAddGapDraftComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      draftId,
      text,
      quote,
    }: {
      draftId: string;
      text: string;
      /** The passage the comment is about; absent for a gap-wide note. */
      quote?: string;
    }) =>
      gapReviewService.addDraftComment(draftId, {
        text,
        ...(quote ? { quote } : {}),
      }),
    onSuccess: (commentsResult, { draftId }) => {
      patchDraftInCachedRuns(queryClient, draftId, (draft) => ({
        ...draft,
        review: { ...draft.review, comments: commentsResult.comments },
      }));
    },
  });
};
